import type { FsrsParams, FsrsSchedule, FsrsState, Rating } from './types';
import {
  DEFAULT_RETENTION_TARGET,
  FSRS_V5_DEFAULT_WEIGHTS,
  MAXIMUM_INTERVAL_DAYS
} from './weights';

const DECAY = -0.5;
const FACTOR = 19 / 81;
const MIN_STABILITY = 0.01;
const MIN_DIFFICULTY = 1;
const MAX_DIFFICULTY = 10;
const MS_PER_DAY = 86_400_000;

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

export function initialStability(w: readonly number[], rating: Rating): number {
  return Math.max(w[rating - 1] ?? MIN_STABILITY, MIN_STABILITY);
}

export function initialDifficulty(w: readonly number[], rating: Rating): number {
  const d = (w[4] ?? 0) - Math.exp((w[5] ?? 0) * (rating - 1)) + 1;
  return clamp(d, MIN_DIFFICULTY, MAX_DIFFICULTY);
}

export function retrievability(elapsedDays: number, stability: number): number {
  if (stability <= 0) return 0;
  return Math.pow(1 + FACTOR * (elapsedDays / stability), DECAY);
}

export function nextInterval(stability: number, retention: number): number {
  const raw = (stability / FACTOR) * (Math.pow(retention, 1 / DECAY) - 1);
  if (!Number.isFinite(raw) || raw < 0) return 1;
  return Math.max(1, Math.round(raw));
}

export function nextDifficulty(
  w: readonly number[],
  d: number,
  rating: Rating
): number {
  const deltaD = -(w[6] ?? 0) * (rating - 3);
  const damped = d + deltaD * ((10 - d) / 9);
  const target = initialDifficulty(w, 4);
  const meanReverted = (w[7] ?? 0) * target + (1 - (w[7] ?? 0)) * damped;
  return clamp(meanReverted, MIN_DIFFICULTY, MAX_DIFFICULTY);
}

export function nextStabilityRecall(
  w: readonly number[],
  d: number,
  s: number,
  r: number,
  rating: Rating
): number {
  const hardPenalty = rating === 2 ? (w[15] ?? 1) : 1;
  const easyBonus = rating === 4 ? (w[16] ?? 1) : 1;
  const gain =
    Math.exp(w[8] ?? 0) *
    (11 - d) *
    Math.pow(s, -(w[9] ?? 0)) *
    (Math.exp((w[10] ?? 0) * (1 - r)) - 1);
  const next = s * (1 + gain * hardPenalty * easyBonus);
  return Math.max(next, MIN_STABILITY);
}

export function nextStabilityForget(
  w: readonly number[],
  d: number,
  s: number,
  r: number
): number {
  const sMin =
    (w[11] ?? 0) *
    Math.pow(d, -(w[12] ?? 0)) *
    (Math.pow(s + 1, w[13] ?? 0) - 1) *
    Math.exp((w[14] ?? 0) * (1 - r));
  return Math.max(Math.min(sMin, s), MIN_STABILITY);
}

export function newFsrsState(): FsrsState {
  return {
    stability: 0,
    difficulty: 0,
    reps: 0,
    lapses: 0,
    state: 'new'
  };
}

export interface ScheduleOptions {
  now?: Date;
  params?: Partial<FsrsParams>;
}

export function scheduleReview(
  current: FsrsState,
  rating: Rating,
  options: ScheduleOptions = {}
): FsrsSchedule {
  const w = options.params?.weights ?? [...FSRS_V5_DEFAULT_WEIGHTS];
  const retention = options.params?.retentionTarget ?? DEFAULT_RETENTION_TARGET;
  const maxInterval = options.params?.maximumInterval ?? MAXIMUM_INTERVAL_DAYS;
  const now = options.now ?? new Date();

  let stability: number;
  let difficulty: number;
  let state: FsrsState['state'];
  let lapses = current.lapses;
  let elapsedDays = 0;

  if (current.state === 'new' || current.stability <= 0) {
    stability = initialStability(w, rating);
    difficulty = initialDifficulty(w, rating);
    state = rating === 1 ? 'learning' : 'review';
  } else {
    if (current.lastReview) {
      elapsedDays = Math.max(
        0,
        (now.getTime() - current.lastReview.getTime()) / MS_PER_DAY
      );
    }
    const r = retrievability(elapsedDays, current.stability);
    difficulty = nextDifficulty(w, current.difficulty, rating);
    if (rating === 1) {
      stability = nextStabilityForget(w, current.difficulty, current.stability, r);
      lapses += 1;
      state = 'relearning';
    } else {
      stability = nextStabilityRecall(
        w,
        current.difficulty,
        current.stability,
        r,
        rating
      );
      state = 'review';
    }
  }

  const intervalDays = Math.min(maxInterval, nextInterval(stability, retention));
  const nextReview = new Date(now.getTime() + intervalDays * MS_PER_DAY);

  return {
    stability,
    difficulty,
    reps: current.reps + 1,
    lapses,
    intervalDays,
    nextReview,
    state
  };
}

export function scheduleToState(
  schedule: FsrsSchedule,
  reviewedAt: Date = new Date()
): FsrsState {
  return {
    stability: schedule.stability,
    difficulty: schedule.difficulty,
    reps: schedule.reps,
    lapses: schedule.lapses,
    lastReview: reviewedAt,
    state: schedule.state
  };
}